import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Tooltip,
    Typography,
    WithStyles
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import React from "react";
import { styles } from "./BasketView.styles";
import { useTranslation } from "react-i18next";
import { BasketViewAddress } from "./BasketViewAddress";
import { BasketViewOverview } from "./BasketViewOverview";
import { CreditCardPayment } from "./CreditCardPayment";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import * as UserDetailsAction from "../../stores/user/UserDetailsAction";
import * as QuotaAction from "../../stores/quotas/QuotaAction";
import { useAuthContext } from "../../auth/AuthProvider";
import { useDispatch, useSelector } from "react-redux";
import { cardPaymentLimit } from "../../constants/CARD_PAYMENT";
import { formatPrice } from "../../utilities/PriceUtils";
import { isEuCountry } from "../../utilities/countriesUtils";
import PermissionGuard from "../components/PermissionGuard/PermissionGuard";
import { AcceptTermsConditionsComponent } from "./AcceptTermsConditionsComponent";
import { LOGOS } from "../../constants/CARD_PAYMENT";
import { selectVisibleCountries } from "../../selectors/countries/CountriesSelector";

/*
    This is the last step of the checkout, here the user must give his/her consent and confirm the order.
*/

export interface IBasketViewOrderProps extends WithStyles<typeof styles> {
    basket: {
        items: [
            id: string,
            type: string,
            product: string,
            from: number,
            to: number,
            price: number
        ],
        totalPrice: number,
        adminFee: number,
        discount: number
        vat: number,
        remark?: string,
    },
    userId: number,
    agrees: any,
    handleAgree: any,
    orderDetails: any,
    handleOrderChange: any,
    userDetails: any,
    checkoutEnabled: boolean,
    showBackendResponseError: boolean,
    setCardError: any,
    cardError: any,
    checkCardValidity: boolean,
    setCheckCardValidity: any,
    setCreditCardValidId: any,
    creditCardValidId: string,
    isCCPaymentProductDisabled: boolean
}

const BasketViewOrderPure: React.FunctionComponent<IBasketViewOrderProps> = ({
                                                                                 classes,
                                                                                 basket,
                                                                                 agrees,
                                                                                 handleAgree,
                                                                                 orderDetails,
                                                                                 handleOrderChange,
                                                                                 checkoutEnabled,
                                                                                 showBackendResponseError,
                                                                                 setCardError,
                                                                                 cardError,
                                                                                 checkCardValidity,
                                                                                 setCheckCardValidity,
                                                                                 setCreditCardValidId, 
                                                                                 creditCardValidId,
                                                                                 isCCPaymentProductDisabled
                                                                             }: IBasketViewOrderProps) => {

    const {t} = useTranslation();

    const authContext = useAuthContext();
    const dispatch = useDispatch();
    const userId = authContext?.user?.id;

    //if we are coming back to this page after an unsuccessful payment, make sure to delete the old payment id
    const paymentId = String(localStorage.getItem("paymentId"));
    if (paymentId !== "null") {
        localStorage.removeItem("paymentId");
    }

    React.useEffect(() => {
        dispatch(UserDetailsAction.requestCurrentUser(userId));
        dispatch(QuotaAction.requestUserQuota(userId));
    }, [dispatch, userId]);

    const userDetails = useSelector(state => state.userDetails?.currentlyLoggedUser);
    const userQuota = useSelector(state => state.quotas?.userQuota);

    const countries = useSelector(state => selectVisibleCountries(state));

    let euCountry = false;
    if (userDetails) {
        euCountry = isEuCountry(countries, userDetails.deliveryAddress.country);
    }

    const isCreditCardShown = true; //configuration.envName !== "Simu"; // CC temporarily disabled for Simu

    return userDetails ? <>
        <Typography className={classes.sectionHeadline} variant="h3">{t("basket.confirmOrder")}</Typography>

        <BasketViewAddress userDetails={userDetails} buttons={false} />

        <BasketViewOverview basket={basket} buttons={false} userId={userId}
                            handleOrderChange={handleOrderChange} comment={false} orderDetails={orderDetails} />


        <Typography variant="h3">{t("basket.chosePayment")}</Typography>
        {isCreditCardShown &&
            <Typography variant="body1">The payment with a credit card is available only for EU countries and for orders under the
                limit
                of {cardPaymentLimit} Euro.</Typography>            
        }
        {isCCPaymentProductDisabled &&
            <Typography variant="body1" className={classes.paymentError}>Credit card payment is currently not possible for the products in your basket.</Typography>
        }
        <FormControl style={{width: "100%"}}>
            <RadioGroup value={orderDetails.paymentMethod} onChange={event => handleOrderChange(event)}>
                <Accordion onClick={event => handleOrderChange(event)}>
                    <AccordionSummary id="panel1bh-header" expandIcon={<ExpandMoreIcon />}>
                        <FormControlLabel name="paymentMethod" value="BankTransfer"
                                          control={<Radio color="primary" onClick={event => handleOrderChange(event)} />}
                                          label={t("basket.bankTransfer")}
                        />
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography>{t("basket.bankTransferText")}</Typography>
                    </AccordionDetails>
                </Accordion>
                {isCreditCardShown &&
                    <Accordion onClick={event => handleOrderChange(event)}
                               disabled={((Number(basket.totalPrice) > cardPaymentLimit) || !euCountry || isCCPaymentProductDisabled)}>
                        <AccordionSummary id="panel2bh-header" expandIcon={<ExpandMoreIcon />}>
                            <FormControlLabel name="paymentMethod" value="Card"
                                            control={<Radio color="primary" onClick={event => handleOrderChange(event)} />}
                                            label={<div>
                                                {t("basket.creditCard")}
                                                <img className={classes.paymentLogo} key={"Mastercard"} src={"../../" + LOGOS.Mastercard} alt={"Mastercard"} width="30" height="20" />
                                                <img className={classes.paymentLogo} key={"Visa"} src={"../../" + LOGOS.Visa} alt={"Visa"} width="30" height="20" />
                                            </div>}
                            />
                        </AccordionSummary>
                        <AccordionDetails>
                            <CreditCardPayment customerName={userDetails.billingAddress.name} checkoutEnabled={checkoutEnabled}
                                               setCardError={setCardError} cardError={cardError} checkCardValidity={checkCardValidity} setCreditCardValidId={setCreditCardValidId} setCheckCardValidity={setCheckCardValidity} creditCardValidId={creditCardValidId} />
                        </AccordionDetails>
                    </Accordion>
                }
                <PermissionGuard scope="orders.quota.checkout">
                    {userQuota && (
                        <Accordion onClick={event => handleOrderChange(event)}>
                            <AccordionSummary id="panel3bh-header" expandIcon={<ExpandMoreIcon />}>
                                <Tooltip
                                    title={
                                        userQuota.remainingAmount < basket.totalPrice ? "Quota has an insufficient limit" : ""
                                    }
                                >
                                    <FormControlLabel
                                        name="paymentMethod"
                                        value="Quota"
                                        control={<Radio color="primary" onClick={event => handleOrderChange(event)} />}
                                        label="Quota"
                                        disabled={userQuota.remainingAmount < basket.totalPrice}
                                    />
                                </Tooltip>
                            </AccordionSummary>
                            <AccordionDetails>
                                <div>
                                    <Typography display="block">
                                        Available quota: {formatPrice(userQuota?.remainingAmount)}
                                    </Typography>
                                    <Typography display="block">Total quota: {formatPrice(userQuota?.totalAmount)}</Typography>
                                    <Typography display="block">Used quota: {formatPrice(userQuota?.usedAmount)}</Typography>
                                </div>
                            </AccordionDetails>
                        </Accordion>
                    )}
                </PermissionGuard>
            </RadioGroup>
        </FormControl>

        <div className={classes.topMargin}>
            <AcceptTermsConditionsComponent agrees={agrees} handleAgree={handleAgree} />
            <Typography variant="body2">{t("basket.requiredFields")}</Typography>
        </div>

        {showBackendResponseError && <div className={classes.topMargin}>
            <Typography variant="body2" className={classes.paymentError} align="right">The selected payment method is not
                available for this order. Please check the total price of your order, your delivery address and VAT
                ID.</Typography>
        </div>}

    </> : <Typography>UserDetails empty</Typography>;
};

export const BasketViewOrder = withStyles(styles)(BasketViewOrderPure);
